/**
 * Semantic status icons — the WCAG 1.4.1 shape contract (docs/design/MERIDIAN.md:1433-1445).
 * State is NEVER color-only: each shape is a distinct silhouette that carries the meaning,
 * and color is a redundant accelerator. These are ALWAYS paired with a visible text label.
 *
 * The shape→meaning mapping is fixed by MERIDIAN and preserved here (redrawn to the 24 grid):
 *   Success  → check-in-circle
 *   Warning  → triangle + bang
 *   Danger   → octagon + X   (MERIDIAN's table names the octagon as the danger silhouette)
 *   Info     → "i"-in-circle
 *   No data  → literal em-dash (means "absent from all feeds", never a blank cell or a zero)
 *
 * Color: these render in currentColor. The CONSUMER sets the semantic token on the parent —
 * --color-success / --color-warning / --color-danger / --color-link / --text-muted — never a
 * raw hex and never a --chart-* value (those are area/stroke fills, not text-contrast colors).
 */
import { Icon, type IconProps } from './Icon';

/** Success / healthy — check inside a circle. Consumer color: --color-success. */
export const StatusSuccess = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.5L11 15.5L16 9" />
  </Icon>
);

/** Warning / degraded — bang inside a triangle. Consumer color: --color-warning. */
export const StatusWarning = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3L2.5 20h19L12 3z" />
    <path d="M12 10v4" />
    <circle cx="12" cy="17" r="0.75" fill="currentColor" stroke="none" />
  </Icon>
);

/** Danger / offline — X inside an octagon. Consumer color: --color-danger. */
export const StatusDanger = (props: IconProps) => (
  <Icon {...props}>
    <path d="M8.5 3h7L21 8.5v7L15.5 21h-7L3 15.5v-7L8.5 3z" />
    <path d="M9.5 9.5L14.5 14.5M14.5 9.5L9.5 14.5" />
  </Icon>
);

/** Info / neutral — "i" inside a circle. Consumer color: --color-link. */
export const StatusInfo = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="8" r="0.75" fill="currentColor" stroke="none" />
  </Icon>
);

/**
 * No data / not checked — a literal em-dash. Per the QC contract, "—" means absent from all
 * feeds and must be visibly distinct from a zero and never a blank cell. Consumer color:
 * --text-muted.
 */
export const NoData = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 12h12" />
  </Icon>
);

/**
 * Loading spinner — a 270° arc. Keeps its rotation via the `.icon-spinner` class (see
 * icons.css), which also carries the prefers-reduced-motion gate (slows, does not stop).
 * Callers get the animation for free by rendering <Spinner /> (the class is applied here);
 * pass a className to extend it — it is merged, not replaced.
 */
export const Spinner = ({ className, ...props }: IconProps) => (
  <Icon className={className ? `icon-spinner ${className}` : 'icon-spinner'} {...props}>
    <path d="M12 3a9 9 0 1 1-9 9" />
  </Icon>
);
